import {
  UserActivityStatus,
  UserSetAvailabilityStatus,
} from '@api/generated/graphql';
import { ServiceUserUtils } from '@modules/ServiceModule/workspace/lib';
import ServiceUser from '@modules/ServiceModule/workspace/types/ServiceUser';
import { tokens } from '@servicetitan/tokens/core';

/**
 * The consolidated statuses for a user (a combination of availability status and activity status)
 */
export enum ConsolidatedStatus {
  Available = 'AVAILABLE',
  OnACall = 'ON_A_CALL',
  OnExternalCall = 'ON_EXTERNAL_CALL',
  AwayFromDesk = 'AWAY_FROM_DESK',
  Break = 'BREAK',
  WrapUp = 'WRAP_UP',
  ExtendedWrapUp = 'EXTENDED_WRAP_UP',
  Offline = 'OFFLINE',
  Unavailable = 'UNAVAILABLE',
}

/**
 * All the consolidated statuses for a user
 */
export const AllConsolidatedStatuses = [
  ConsolidatedStatus.Available,
  ConsolidatedStatus.OnACall,
  ConsolidatedStatus.OnExternalCall,
  ConsolidatedStatus.WrapUp,
  ConsolidatedStatus.ExtendedWrapUp,
  ConsolidatedStatus.AwayFromDesk,
  ConsolidatedStatus.Break,
  ConsolidatedStatus.Unavailable,
  ConsolidatedStatus.Offline,
] as const;

/**
 * The consolidated statuses that are considered unavailable
 */
export const UnavailableConsolidatedStatuses = [
  ConsolidatedStatus.OnACall,
  ConsolidatedStatus.OnExternalCall,
  ConsolidatedStatus.WrapUp,
  ConsolidatedStatus.ExtendedWrapUp,
  ConsolidatedStatus.AwayFromDesk,
  ConsolidatedStatus.Break,
  ConsolidatedStatus.Unavailable,
  ConsolidatedStatus.Offline,
] as const;

const _sortOrderMap = new Map<ConsolidatedStatus, number>(
  AllConsolidatedStatuses.map((status, index) => [status, index]),
);

/**
 * Gets the sort order for a consolidated status
 * @param status - The consolidated status to get the sort order for
 * @returns The sort order for the consolidated status
 */
export const getConsolidatedStatusSortOrder = (status: ConsolidatedStatus) => {
  return _sortOrderMap.get(status) ?? 0;
};

const _unavailableConsolidatedStatusesSet = new Set<ConsolidatedStatus>(
  UnavailableConsolidatedStatuses,
);

/**
 * Checks if a consolidated status is an unavailable status
 * @param status - The consolidated status to check
 * @returns True if the consolidated status is an unavailable status, false otherwise
 */
export const isUnavailableConsolidatedStatus = (status: ConsolidatedStatus) => {
  return _unavailableConsolidatedStatusesSet.has(status);
};

/**
 * Gets a consolidated status for a user based on their activity status and status
 * @param user - The user to get the consolidated status for
 * @returns The consolidated status for the user
 */
export const getConsolidatedStatus = (
  user: ServiceUser,
): ConsolidatedStatus => {
  // edge case where user is null
  if (!user) return ConsolidatedStatus.Unavailable;
  return calculateConsolidatedStatus({
    activityStatus: ServiceUserUtils.getActivityStatus(user),
    availabilityStatus: ServiceUserUtils.getAvailabilityStatus(user),
  });
};

/**
 * Calculates a consolidated status for activity status and availability status
 * @param activityStatus - The activity status to get the consolidated status for
 * @param availabilityStatus - The availability status to get the consolidated status for
 * @returns The consolidated status for the activity status and availability status
 */
export const calculateConsolidatedStatus = ({
  activityStatus,
  availabilityStatus,
}: {
  activityStatus: UserActivityStatus;
  availabilityStatus: UserSetAvailabilityStatus;
}): ConsolidatedStatus => {
  switch (activityStatus) {
    // some activity statuses take precedence over the availability status
    case UserActivityStatus.OnACall:
      return ConsolidatedStatus.OnACall;
    case UserActivityStatus.OnExternalCall:
      return ConsolidatedStatus.OnExternalCall;
    case UserActivityStatus.WrapUp:
      return ConsolidatedStatus.WrapUp;

    // other statuses will use the availability status
    case UserActivityStatus.CallOffer:
    case UserActivityStatus.None:
    default:
      break;
  }

  switch (availabilityStatus) {
    case UserSetAvailabilityStatus.Available:
      return ConsolidatedStatus.Available;
    case UserSetAvailabilityStatus.AwayFromDesk:
      return ConsolidatedStatus.AwayFromDesk;
    case UserSetAvailabilityStatus.Break:
      return ConsolidatedStatus.Break;
    case UserSetAvailabilityStatus.ExtendedWrapUp:
      return ConsolidatedStatus.ExtendedWrapUp;
    case UserSetAvailabilityStatus.Offline:
      return ConsolidatedStatus.Offline;
    case UserSetAvailabilityStatus.Unavailable:
      return ConsolidatedStatus.Unavailable;
    default:
      break;
  }

  // fallback to unavailable
  return ConsolidatedStatus.Unavailable;
};

/**
 * Gets the UI display text for a consolidated status
 * @param status - The consolidated status to get the display text for
 * @returns The display text for the consolidated status
 */
export const getConsolidatedStatusDisplayText = (
  status: ConsolidatedStatus,
) => {
  switch (status) {
    case ConsolidatedStatus.Available:
      return 'Available';
    // requirements specify that both on a call and on external call should be displayed as "On a Call"
    case ConsolidatedStatus.OnExternalCall:
    case ConsolidatedStatus.OnACall:
      return 'On a Call';
    case ConsolidatedStatus.AwayFromDesk:
      return 'Away From Desk';
    case ConsolidatedStatus.Break:
      return 'Break';
    case ConsolidatedStatus.WrapUp:
      return 'Wrap Up';
    case ConsolidatedStatus.ExtendedWrapUp:
      return 'Extended Wrap Up';
    case ConsolidatedStatus.Offline:
      return 'Offline';
    case ConsolidatedStatus.Unavailable:
      return 'Unavailable';
    default:
      return 'Unknown';
  }
};

/**
 * Gets the UI (dot) color for a consolidated status [hex]
 * @param status - The consolidated status to get the color for
 * @returns The color for the consolidated status
 */
export const getConsolidatedStatusColor = (
  status: ConsolidatedStatus,
): string => {
  switch (status) {
    case ConsolidatedStatus.Available:
      return tokens.colorGreen400;
    case ConsolidatedStatus.OnACall:
    case ConsolidatedStatus.OnExternalCall:
      return tokens.colorBlue400;
    case ConsolidatedStatus.AwayFromDesk:
      return tokens.colorOrange400;
    case ConsolidatedStatus.Break:
      return tokens.colorPurple400;
    case ConsolidatedStatus.WrapUp:
      return tokens.colorMagenta400;
    case ConsolidatedStatus.ExtendedWrapUp:
      return tokens.colorCyan400;
    case ConsolidatedStatus.Unavailable:
      return tokens.colorRed400;
    case ConsolidatedStatus.Offline:
    default:
      return tokens.colorBlueGrey600;
  }
};
